/**
 * The exercise registry: which drills this app can score, and what each one
 * needs from the render loop.
 *
 * Why this exists. Everything under src/metrics/ splits cleanly into two halves
 * that used to be welded together in index.js. One half is generic — the pose
 * geometry, the sample, the frame buffer, the smoothing, the window metrics, the
 * readout, the sounds, the countdown, the receipt. The other half is one
 * exercise: that a rep is a full revolution of the arm, that six criteria are
 * worth checking, and what to say when one of them fails. Only the second half
 * changes between drills, so an exercise is a declaration of exactly that:
 *
 *   id            stable, used by `?exercise=` and written into the receipt
 *   name, blurb   what the menu shows
 *   view          which camera view its criteria are honest in
 *   calibrated    whether its thresholds were measured or guessed
 *   cues          what to say when a criterion fails, by criterion id
 *   createCounter a rep counter: `push(sample, side)`, `reset()`, `count`,
 *                 `progress`, `break()`
 *   scoreRep      one rep's samples in, criterion rows out
 *
 * A row is `{ id, label, value, unit, precision, pass }`, with `pass` null for
 * "could not judge this" and an optional `informational: true` for a figure that
 * reports without judging. Everything downstream — the verdict, the sounds, the
 * readout, the tally, the receipt — reads only that shape, so an exercise that
 * produces it needs no changes anywhere else.
 *
 * What an exercise may NOT do is claim to be calibrated. The swing's thresholds
 * came off real footage and are recorded in docs/arm-swing-posture.md; the
 * squat jump's counting was measured the same way in docs/squat-jump.md but its
 * criteria are anatomy and judgement, and the V-sit's are judgement throughout.
 * They all score reps and only one of them means what a score usually means, so
 * the flag is carried through to the menu and printed on the receipt rather than
 * left in a comment.
 */
import { armSwing } from "./arm_swing";
import { squatJump } from "./squat_jump";
import { vSit } from "./v_sit";
import { wallHandstand } from "./wall_handstand";
import { metricsForView } from "../metrics/definitions";

/** Menu order: the calibrated drill first, because it is the one to trust. */
export const EXERCISES = [armSwing, vSit, squatJump, wallHandstand];

export const DEFAULT_EXERCISE = armSwing.id;

export function exerciseById(id) {
  return EXERCISES.find((exercise) => exercise.id === id) || null;
}

/**
 * The window metrics to show for one exercise.
 *
 * The view gate in metrics/definitions.js answers "is this figure honest from
 * this angle", which is a different question from "does this figure say anything
 * about this exercise". Swing range is honest side-on and irrelevant to a squat.
 * An exercise narrows the list with `metricIds`; without one it gets everything
 * its view allows, which is what the arm swing has always shown.
 */
export function metricsFor(exercise, view) {
  const metrics = metricsForView(view);
  if (exercise == null || exercise.metricIds == null) {
    return metrics;
  }
  return metrics.filter((metric) => exercise.metricIds.includes(metric.id));
}
